import Analytics from "../Analytics";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import find from "lodash/find";
import {
  BRAND_NAME,
  CONTRACT_TYPES,
  COUNTRY,
  GTM_ID,
  SIM_TYPES
} from "utils/constants";
import {
  ANALYTICS_TYPES,
  GTM_DATALAYER_EVENT_MAPPING,
  GTM_EVENTS_NAME
} from "../../constants/AnalyticsTypeConstants";
import TagManager from "react-gtm-module";
import {
  compactJoin,
  getChannelFromUrl,
  getURLParams,
  isMobile
} from "utils/helpers";
import Session from "session";

class GoogleTagManagerAnalytics extends Analytics {
  pushEvent(event, data, message, country = COUNTRY.toLowerCase()) {
    /**
     * Push events to Google Tag Manager
     * @param {string} event type of event triggered based on user actions, page load and etc.
     * @param {object} data data associated with triggered event
     */
    const currentUrl = get(window, "location.pathname", "");
    if (event === GTM_EVENTS_NAME.PAGE_VISIT) {
      this.pushPageVisit(data, event);
    } else {
      this.pushToDataLayer(event, data, currentUrl, country, GTM_ID);
    }
  }

  pushPageVisit(data, event) {
    if (GTM_ID) {
      const dataLayer = buildPageVisitEventDataLayer(data, event);
      TagManager.dataLayer({ dataLayer });
    }
  }

  pushToDataLayer(
    event,
    data,
    currentUrl,
    country,
    googleTagManagerId = get(window, "GOOGLE_TAG_MANAGER_ID"),
    tagManager = TagManager
  ) {
    /**
     * Pushes Analytics events to Google Tag Manager
     * @param {string} event type of event triggered based on user actions, page load and etc.
     * @param {object} data data associated with triggered event
     * @param {string} country current country
     * @param {string} googleTagManagerId account ID of Google Tag Manager, initialized through ConfigManager
     */
    const gtmDataLayerEvent = GTM_DATALAYER_EVENT_MAPPING[event];

    // check if event needs to trigger google tag manager
    if (gtmDataLayerEvent) {
      // check if current country uses google tag manager
      const countrySupported = find(gtmDataLayerEvent, country);

      if (countrySupported) {
        // check if google tag manager needs to trigger on currentUrl
        const urlSupported = find(gtmDataLayerEvent.valid_countries[country], [
          "trigger_urls",
          [currentUrl]
        ]);

        if (urlSupported) {
          // push to dataLayer only if google tag manager ID exists
          if (googleTagManagerId) {
            const dataLayerParams = constructDataLayerParams(
              data,
              urlSupported?.event
            );
            const gtmEvent = dataLayerParams?.event || urlSupported?.event;
            const tagManagerArgs = {
              dataLayer: dataLayerParams
            };

            this.logEventStart(
              ANALYTICS_TYPES.gtm,
              gtmEvent,
              "GoogleTagManager",
              dataLayerParams
            );
            // push to Google Tag Manager
            tagManager.dataLayer(tagManagerArgs);
            console.log(
              `Pushed GoogleTagManagerEvent ${gtmEvent} through GTM_MODULE`,
              dataLayerParams
            );
            this.logEventFinish(
              ANALYTICS_TYPES.gtm,
              gtmEvent,
              "GoogleTagManager",
              dataLayerParams
            );
          }
        }
      }
    }
  }
}

export const constructDataLayerParams = (
  data,
  event = GTM_EVENTS_NAME.ECOMM_EVENT
) => {
  /**
   * Constructs data to be pushed into Google Tag Manager
   * @param {object} data Data associated with event
   * @return {object} Data to be pushed into Google Tag Manager
   */

  switch (event) {
    case GTM_EVENTS_NAME.ECOMM_EVENT:
      return buildEcommDataLayer(data, event);
    case GTM_EVENTS_NAME.CUSTOM_PURCHASE:
    case GTM_EVENTS_NAME.ORDER_PURCHASE:
    case GTM_EVENTS_NAME.PURCHASE:
    case GTM_EVENTS_NAME.ADDONS_SELECTED:
    case GTM_EVENTS_NAME.PERSONAL_DETAIL_SUBMIT:
      return buildPurchaseEventDataLayer(data, event);
    case GTM_EVENTS_NAME.SIM_VERIFICATION:
    case GTM_EVENTS_NAME.SIM_VERIFICATION_CLICK:
    case GTM_EVENTS_NAME.NUMBER_SELECTED_PAGE_VIEW:
    case GTM_EVENTS_NAME.CURRENT_TELCO:
    case GTM_EVENTS_NAME.PLAN_TYPE:
    case GTM_EVENTS_NAME.NRIC_STATUS:
    case GTM_EVENTS_NAME.EXISTING_NUMBER_ENTERED:
    case GTM_EVENTS_NAME.NEW_NUMBER_SELECTED:
    case GTM_EVENTS_NAME.PROMO_CODE_ENTERED:
      return buildFlowEventDataLayer(data, event);
    case GTM_EVENTS_NAME.SADPURCHASE:
      return buildSadPurchaseEventDataLayer(data, event);
    case GTM_EVENTS_NAME.ACTIVATE:
      return buildActivateEventDataLayer(data, event);
    case GTM_EVENTS_NAME.PROMO_CODE_APPLIED:
      return buildPromoCodeAppliedEventDataLayer(data, event);
    default:
      break;
  }

  return {};
};

const buildEcommDataLayer = (data, event) => ({
  event,
  transactionId: get(data, "order_ref", null), // going into dataLayer, therefore fallback on null value
  transactionProductType: get(data, "plan_name", null), // going into dataLayer, therefore fallback on null value
  transactionTotal: get(data, "orderParams.order_summary.onetime_total_fee", 0),
  transactionQuantity: 1
});

const buildFlowEventDataLayer = (data, event) => {
  const { formValues, selected_number, referral_code } = data || {};
  const { number_type, new_selected_number } = formValues || {};
  const customerID = Session.getDeviceId();
  return Object.fromEntries(
    Object.entries({
      event,
      flowChannel: getChannelFromUrl(),
      customerID,
      coupon: referral_code,
      numberType: number_type,
      selectedNumber: selected_number?.number ?? new_selected_number
    }).filter(([_, value]) => value != null) // Removes null and undefined values
  );
};

const buildPurchaseEventDataLayer = (data, event) => {
  const {
    channel,
    plan_name,
    order_ref,
    order,
    orderParams,
    currency,
    referral_code,
    referral_code_type,
    payment_status,
    is_multi_sim,
    family_plan_sim_count,
    planService,
    customer_id,
    contact_email,
    contact_number,
    order_deliver_detail
  } = data || {};
  const {
    channel_name,
    sim_count,
    selected_number,
    msisdn_source,
    portin_detail,
    phone_only,
    id_upload_pending,
    order_summary,
    user_detail,
    billing_detail,
    deliver_detail,
    order_user_detail_attributes,
    order_deliver_detail_attributes
  } = orderParams || {};
  const { onetime_total_fee, delivery_slot_fee } = order_summary || {};
  const orderRef = order_ref || order?.order_ref;
  const orderChannel = channel || channel_name;
  const orderSimCount = is_multi_sim ? 2 : sim_count || 1;
  const simCount = family_plan_sim_count
    ? family_plan_sim_count
    : orderSimCount;
  const shipping =
    delivery_slot_fee || data?.delivery_slot_fee || order?.delivery_slot_fee;
  const isPortIn = msisdn_source === CONTRACT_TYPES.PORT_IN;
  const isESim = orderChannel === SIM_TYPES.ESIM;
  const customerID = customer_id || Session.getDeviceId();
  let user = !isEmpty(user_detail) ? user_detail : order_user_detail_attributes;
  user = !isEmpty(user)
    ? user
    : { email: contact_email, phone_number: contact_number };
  let address = !isEmpty(billing_detail) ? billing_detail : deliver_detail;
  address = !isEmpty(address) ? address : order_deliver_detail_attributes;
  address = !isEmpty(address) ? address : order_deliver_detail;
  return {
    event,
    orderChannel,
    flowChannel: getChannelFromUrl(),
    idUploadPending: id_upload_pending,
    planName: plan_name,
    phoneOnly: phone_only,
    orderRef,
    customerID,
    purchaseID: orderRef,
    transactionId: orderRef,
    value: onetime_total_fee,
    orderSummary: order_summary,
    tax: planService?.tax_percentage || 0,
    shipping,
    currency,
    coupon: referral_code,
    couponType: referral_code_type,
    simCount: phone_only ? 0 : simCount,
    simType: isESim ? SIM_TYPES.ESIM : SIM_TYPES.PHYSICAL_SIM,
    numberType: msisdn_source,
    portinDonor: isPortIn ? portin_detail?.port_donor : "",
    portinType: isPortIn
      ? portin_detail?.is_postpaid
        ? "postpaid"
        : "prepaid"
      : "",
    selectedNumber: isPortIn ? portin_detail?.port_number : selected_number,
    journeyType: "Non-SAD New",
    paymentStatus: payment_status,
    items: constructItems(data),
    ...constructEnhancedConversionData(user, address)
  };
};

const buildSadPurchaseEventDataLayer = (data, event) => {
  const purchaseData = buildPurchaseEventDataLayer(data, event);
  return {
    ...purchaseData,
    sadPurchaseID: purchaseData?.purchaseID,
    journeyType: "SAD",
    paymentStatus: "n/a"
  };
};

const buildActivateEventDataLayer = (data, event) => {
  const purchaseData = buildPurchaseEventDataLayer(data, event);
  const { success, sim_activated } = data || {};
  const activationStatus = success || sim_activated ? "success" : "failed";
  return {
    ...purchaseData,
    sadPurchaseID: purchaseData?.purchaseID,
    journeyType: "SAD",
    activationStatus
  };
};

const buildPromoCodeAppliedEventDataLayer = (data, event) => {
  const { referral_code, referral_code_type, plan_name, reason } = data || {};
  const valid =
    data?.valid || (!reason && !!referral_code_type && !!referral_code);
  const eventName = valid
    ? `${referral_code_type}_code_applied`
    : "invalid_code_applied";
  return {
    event: eventName,
    flowChannel: getChannelFromUrl(),
    coupon: referral_code || "N/A",
    couponType: referral_code_type || "N/A",
    planName: plan_name || "N/A",
    reason: reason || "N/A",
    valid
  };
};

const buildPageVisitEventDataLayer = (_, event) => {
  return {
    event,
    urlParams: getURLParams(),
    env: isMobile() ? "mobile" : "desktop",
    host: window.location.host,
    path: window.location.pathname,
    sessionId: Session.getDeviceId(),
    ipAddress: Session.getIpAddress()
  };
};

const constructItems = data => {
  const { sku, textName, basicPrice } = data?.planService || {};
  const orderItems =
    data?.orderParams?.order_items_attributes ||
    data?.orderParams?.order_items ||
    [];

  const planItem = {
    item_name: textName,
    item_id: sku,
    price: basicPrice,
    item_brand: BRAND_NAME,
    item_category: "PLAN",
    item_variant: "PLAN",
    quantity: "1"
  };

  const items = !isEmpty(orderItems)
    ? orderItems.map(item => ({
        item_name: item?.title || item?.name || "",
        item_id: item?.sku || "",
        price: item?.price || 0,
        item_brand: BRAND_NAME,
        item_category: item?.type || "ADDON",
        item_variant: item?.type || "ADDON",
        quantity: "1"
      }))
    : [];

  items.unshift(planItem);

  return items;
};

const constructEnhancedConversionData = (user, address) => {
  if (isEmpty(user) && isEmpty(address)) return {};
  const { email, phone_number, first_name, last_name } = user || {};
  const { zip_code, country, city, hse_blk_tower, street_building_name } =
    address || {};
  const street = compactJoin([hse_blk_tower, street_building_name], " ");
  const enhanced_conversion_data = {
    customerBillingEmail: email || "",
    customerBillingPhone: phone_number || "",
    customerBillingFirstName: first_name || "",
    customerBillingLastName: last_name || "",
    customerBillingStreet: street || "",
    customerBillingCity: city || "",
    customerBillingRegion: city || "",
    customerBillingCountry: country || "",
    customerBillingPostalCode: zip_code || ""
  };
  return { enhanced_conversion_data };
};

export default new GoogleTagManagerAnalytics();
